import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Input, notification, Skeleton, Tag, Space, Drawer } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import {
    getRecipes,
    getRecipeStores,
    markRecipeOutOfStock,
    restockRecipe,
    getMenusByRecipe
} from '../../services/outofstockservice';
import { RecipeItem, StoreStatus, MenuLinkedDto } from '../../types/outofstocktypes';
import StoreSelectorDrawer from './storeselectordrawer';
import { useGlobalLoading } from '../common/globalloadingcontext';
import FormButton from '../common/formbutton';

const { Search } = Input;

interface RecipesTabProps {
    userGuid: string;
    activeTab: string;
}

const RecipesTab: React.FC<RecipesTabProps> = ({ userGuid, activeTab }) => {
    const { setGlobalLoading } = useGlobalLoading();
    const [recipeData, setRecipeData] = useState<RecipeItem[]>([]);
    const [recipeTotalCount, setRecipeTotalCount] = useState(0);
    const [recipeLoading, setRecipeLoading] = useState(false);
    const [recipeSearch, setRecipeSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Drawer state for store selector
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState<RecipeItem | null>(null);
    const [storesData, setStoresData] = useState<StoreStatus[]>([]);
    const [currentAction, setCurrentAction] = useState<'out' | 'restock' | null>(null);

    // Drawer state for linked menus
    const [linkedMenusDrawerVisible, setLinkedMenusDrawerVisible] = useState(false);
    const [linkedMenus, setLinkedMenus] = useState<MenuLinkedDto[]>([]);
    const [selectedRecipeForMenus, setSelectedRecipeForMenus] = useState<RecipeItem | null>(null);

    const fetchRecipes = async (pageNumber = 1, size = pageSize, search = recipeSearch) => {
        setRecipeLoading(true);
        try {
            const res = await getRecipes(pageNumber, size, search);
            setRecipeData(res.data.items);
            setRecipeTotalCount(res.data.totalCount);
            setCurrentPage(pageNumber);
        } catch (err) {
            console.error(err);
            notification.error({ message: 'Error', description: 'Failed to load recipes.' });
        } finally {
            setRecipeLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setRecipeSearch(value);
            fetchRecipes(1, pageSize, value);
        }, 400),
        [pageSize]
    );

    useEffect(() => {
        if (activeTab === 'recipes') {
            fetchRecipes(currentPage, pageSize, recipeSearch);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchRecipes(currentPage, pageSize, recipeSearch);
    }, [pageSize]);

    const handleManageStores = async (recipe: RecipeItem, action: 'out' | 'restock') => {
        setDrawerLoading(true);
        setDrawerVisible(true);
        setSelectedRecipe(recipe);
        setCurrentAction(action);

        try {
            const res = await getRecipeStores(
                recipe.recipeGuid,
                undefined,
                action === 'out' ? 'InStock' : 'OutOfStock'
            );
            setStoresData(res.data);
        } catch (err) {
            console.error(err);
            notification.error({ message: 'Error', description: 'Failed to load stores.' });
        } finally {
            setDrawerLoading(false);
        }
    };

    const handleConfirmStores = async (selectedStoreGuids: string[], action: 'out' | 'restock') => {
        if (!selectedRecipe) return;

        setGlobalLoading(true);
        try {
            const request = {
                recipeGuid: selectedRecipe.recipeGuid,
                storeGuids: selectedStoreGuids,
                userGuid: userGuid,
                reason: action === 'out' ? 'Marked out of stock via UI' : 'Restocked via UI',
            };

            if (action === 'out') {
                await markRecipeOutOfStock(request);
            } else {
                await restockRecipe(request);
            }

            await fetchRecipes(currentPage, pageSize, recipeSearch);

            notification.success({
                message: 'Success',
                description: `${selectedRecipe.name} updated for ${selectedStoreGuids.length} stores (action: ${action}).`,
            });
        } catch (err) {
            console.error(err);
            notification.error({
                message: 'Error',
                description: `Failed to update ${selectedRecipe.name}.`,
            });
        } finally {
            setGlobalLoading(false);
            setDrawerVisible(false);
            setSelectedRecipe(null);
        }
    };

    const handleViewLinkedMenus = async (recipe: RecipeItem) => {
        setSelectedRecipeForMenus(recipe);
        setLinkedMenusDrawerVisible(true);
        try {
            const res = await getMenusByRecipe(recipe.recipeGuid);
            setLinkedMenus(res.data);
        } catch (err) {
            console.error(err);
            notification.error({ message: 'Error', description: 'Failed to load linked menus.' });
        }
    };

    const recipeColumns: ColumnsType<RecipeItem> = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Stores',
            key: 'stores',
            render: (_: unknown, record: RecipeItem) => (
                <Space style={{ display: 'flex', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 120 }}>Total Stores: {record.totalStores}</div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                        <Tag color="green">Available in {record.inStockCount} stores</Tag>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <Tag color="red">Out of stock in {record.outOfStockCount} stores</Tag>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'manage',
            render: (_: unknown, record: RecipeItem) => (
                <Space>
                    <FormButton
                        title="View Linked Menus"
                        onClick={() => handleViewLinkedMenus(record)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col>
                    <Search
                        placeholder="Search recipes"
                        onChange={e => debouncedSearch(e.target.value)}
                        style={{ width: 350 }}
                        allowClear
                    />
                </Col>
            </Row>

            {recipeLoading && recipeData.length === 0 ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
                <Table<RecipeItem>
                    dataSource={recipeData}
                    columns={recipeColumns}
                    loading={recipeLoading && recipeData.length > 0}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: recipeTotalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
                        onChange: (page, size) => {
                            setPageSize(size);
                            fetchRecipes(page, size, recipeSearch);
                        },
                    }}
                    rowKey="recipeGuid"
                />
            )}

            {selectedRecipe && currentAction && (
                <StoreSelectorDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onOk={handleConfirmStores}
                    itemName={selectedRecipe.name} // renamed prop
                    stores={storesData}
                    loading={drawerLoading}
                    action={currentAction}
                />
            )}

            <Drawer
                title={`Linked Menus for ${selectedRecipeForMenus?.name}`}
                width={650}
                onClose={() => setLinkedMenusDrawerVisible(false)}
                open={linkedMenusDrawerVisible}
                styles={{
                    header: {
                        color: 'white',
                        backgroundColor: 'var(--color-primary)',
                        fontWeight: 350,
                        fontSize: '14px',
                        border: 0,
                        minHeight: '40px',
                    },
                }}
            >
                <Table<MenuLinkedDto>
                    dataSource={linkedMenus}
                    columns={[
                        { title: 'Menu Name', dataIndex: 'name', key: 'name' },
                        { title: 'Menu Guid', dataIndex: 'menuGuid', key: 'menuGuid' },
                    ]}
                    rowKey="menuGuid"
                    pagination={{ pageSize: 10 }}
                />
            </Drawer>
        </>
    );
};

export default RecipesTab;